import { useQuery } from '@tanstack/react-query';
import { BuildingLibraryIcon } from '@heroicons/react/24/outline';
import { teamApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';

/**
 * Read-only indicator of the active bank a feature operates on.
 *
 * Replaces the old per-feature "pick any team" dropdowns on the upload/analysis
 * screens (ALERT, HMDA, Marketing). Those let a multi-bank user run an analysis
 * as a team OTHER than their active one — a wrong-bank footgun. The active bank
 * is now authoritative everywhere and is changed only via the global
 * TeamSwitcher in the sidebar. Renders nothing for single-bank users.
 */
export default function ActiveBankNotice({ className = '' }: { className?: string }) {
  const { teamId } = useTeamId();
  const { data } = useQuery({
    queryKey: ['teams', 'list-unwrapped'],
    queryFn: async () => (await teamApi.getTeams()).data,
    staleTime: 5 * 60 * 1000,
  });
  const teams = data?.teams || [];
  if (teams.length <= 1) return null;

  const active = teams.find((t: { id: string }) => t.id === teamId);
  return (
    <div
      className={`flex items-center gap-2 rounded-lg border border-ocean-200 bg-ocean-50 px-3 py-2 text-sm ${className}`}
    >
      <BuildingLibraryIcon className="h-4 w-4 text-ocean-600 shrink-0" />
      <span className="text-gray-700">
        Working in <span className="font-semibold text-navy-900">{active?.name ?? '—'}</span>. Switch
        banks using the selector in the sidebar.
      </span>
    </div>
  );
}
